// @ts-strict-ignore
import React, { useCallback, useContext, useState } from 'react';
import PlusCircleIcon from '@shared/assets/icons/v2/plus-circle.svg';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { LinkedCard } from '@shared/components/blocks/lib/cards';
import { useOpenLinkedCard, useOpenNewLinkedCard } from '@shared/components/blocks/lib/cards/hooks';
import { InCardsSheet } from '@shared/components/blocks/lib/inCards/sheet';
import { MBBottomSheet } from '@shared/components/bottomSheet';
import { MBChannelImage } from '@shared/components/image';
import { FIXED_WIDTH } from '@shared/components/multiSelect';
import { MBTouchableIcon, MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { useChannel, useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { usePermission } from '@shared/util/permissions/hooks';

interface Props {
  entry: MBAPI.ListRelatedCardRollupsQuery_listRelatedCardRollups;
  cardId: string;
  channelId: number;
}

export function MBInCardsRollupCount({ entry, cardId, channelId }: Props) {
  const channel = useChannel({ channelId: entry.channelId });
  const colors = useContext(ColorThemeContext);
  const updatePermission = usePermission({ action: 'update', subject: 'Card', channelId });
  const [showList, setShowList] = useState<boolean>(false);
  const openLinkedCard = useOpenLinkedCard();
  const openNewRelationCard = useOpenNewLinkedCard({ cardId, externalChannelId: channelId });
  const closeSheet = useCallback(() => setShowList(false), [setShowList]);

  const onPressCard = useCallback<(cards: LinkedCard[]) => void>(
    cards => {
      closeSheet();
      openLinkedCard(cards[0]);
    },
    [openLinkedCard, closeSheet],
  );

  const onCreateFromSheet = useCallback(
    (query: string) => {
      closeSheet();
      openNewRelationCard({ name: query });
    },
    [openNewRelationCard, closeSheet],
  );

  const onCreateFromAddButton = useCallback(() => {
    openNewRelationCard();
  }, [openNewRelationCard]);

  const fixedWidth = useIsDesktop() ? FIXED_WIDTH : null;

  const anchorName = `--in-cards-block-${entry.channelId}`;

  return (
    <>
      <MBTouchableOpacity onPress={() => setShowList(true)} disabled={entry.cardCount === 0}>
        <View
          row
          centerVertically
          style={{ justifyContent: 'space-between', anchorName }}
          paddingVertical={1}
        >
          <MBChannelImage
            style={{ width: grid(4), height: grid(4) }}
            channel={{
              ...channel,
              imageUrl: entry.channelImageUrl,
              name: entry.channelName,
              type: 'CARD_GENERIC',
            }}
          />
          <View paddingLeft={1}>
            <MBTextOverlineBold color={colors.foregroundInactive}>
              {entry.channelName}
            </MBTextOverlineBold>
            <MBTextBody1>
              {i18n('card.blocks.inCards.cards', { count: entry.cardCount, max: 10000 })}
            </MBTextBody1>
          </View>
          <View flex />
          {updatePermission.enabled && (
            <MBTouchableIcon
              onPress={onCreateFromAddButton}
              color={colors.foregroundActive}
              noPadding
              source={PlusCircleIcon}
            />
          )}
        </View>
      </MBTouchableOpacity>
      {showList && (
        <MBBottomSheet
          anchorName={anchorName}
          style={{ minWidth: fixedWidth?.width, minHeight: grid(48) }}
          title={entry.channelName}
          onClose={() => setShowList(false)}
        >
          <MBView style={fixedWidth} fill>
            <InCardsSheet
              onPress={onPressCard}
              cardId={cardId}
              channelId={entry.channelId}
              channelName={entry.channelName}
              onAdd={onCreateFromSheet}
            />
          </MBView>
        </MBBottomSheet>
      )}
    </>
  );
}
